/** Routes of the auth micro-service (mounted under `/v1/auth`). */
export const endpoints = {
  auth: {
    login: '/v1/auth/login',
    logout: '/v1/auth/logout',
    refresh: '/v1/auth/refresh',
    changePassword: '/v1/auth/change-password',
  },
} as const

/** Routes of the main NexQr API, which owns the user profile. */
export const mainEndpoints = {
  profile: '/v1/profiles',
  /**
   * The catalogue, scoped to the signed-in merchant's tenant.
   *
   * `/v1/merchant`, not `/v1/pos`. The two trees carry the same controllers over
   * the same services — PosGuard and MerchantGuard resolve the tenant through
   * one owner/admin rule — but the POS tree is the till's API and answers to a
   * tablet's needs. The Back Office reads its own.
   */
  products: {
    root: '/v1/merchant/products',
    variants: '/v1/merchant/products/variants',
    /** The blank workbook: headings, dropdowns, and this tenant's own names. */
    exportTemplate: '/v1/merchant/products/export/template',
    /** The catalogue in that same workbook — edit it and send it back. */
    export: '/v1/merchant/products/export',
    import: '/v1/merchant/products/import',
    /** The dry run. Same answer as `import`, with nothing written. */
    importValidate: '/v1/merchant/products/import/validate',
    /** Bulk outlet links — both add to or take from what each product has, never replace. */
    linkBranches: '/v1/merchant/products/branches/link',
    unlinkBranches: '/v1/merchant/products/branches/unlink',
  },
  categories: '/v1/merchant/categories',
  /**
   * Product brands (Phase 1.6.5). Same five routes as categories: a paged list
   * that also takes `isActive`, and `:id` for one brand's read, write and delete.
   * Delete is refused while any product still carries the brand.
   */
  brands: '/v1/merchant/brands',
  choiceGroups: '/v1/merchant/choice-groups',
  /**
   * Staff & Employees, scoped to the signed-in merchant's membership.
   *
   * These go a step further than the rest of `/v1/merchant`: they are resolved by
   * the membership-aware staff guard, which reads module levels and branch scope
   * rather than just "does this user own a tenant".
   */
  staff: {
    merchantUsers: '/v1/merchant/staff/merchant-users',
    roles: '/v1/merchant/staff/roles',
    permissions: '/v1/merchant/staff/permissions',
    branches: '/v1/merchant/staff/branches',
    invitations: '/v1/merchant/staff/invitations',
    acceptInvitation: '/v1/merchant/staff/invitations/accept',
    /** Public — the token in the body is the credential. */
    previewInvitation: '/v1/merchant/staff/invitations/preview',
    /** Public — creates the invitee's account, then links it. */
    acceptInvitationSignUp: '/v1/merchant/staff/invitations/accept/sign-up',
    /** Employees & PINs — the outlet-level directory the POS tablet reads. */
    employees: '/v1/merchant/staff/employees',
    employeeRoles: '/v1/merchant/staff/employee-roles',
  },
  /**
   * ABA partner integration, scoped to the signed-in merchant's tenant.
   *
   * Under `/v1/merchant`, not `/v1/pos`: registering a business with ABA and
   * linking its credentials is Back Office work, not something a till does. The
   * `/v1/pos` twin still answers identically — same service, same owner/admin
   * rule — and is deprecated.
   */
  paymentPartner: {
    root: '/v1/merchant/payment-partner',
    current: '/v1/merchant/payment-partner/current',
    lookup: '/v1/merchant/payment-partner/lookup',
    link: '/v1/merchant/payment-partner/link',
  },
  /**
   * The rest of the Back Office tree. Every one of these is mounted by its own
   * module under `/v1/merchant` — see each `*.module.ts` on the server, which
   * registers the segment rather than the controller declaring it.
   */
  optionSets: '/v1/merchant/product-option-set',
  discounts: {
    root: '/v1/merchant/discounts',
    /** The pickable catalogue for a discount — products, not discounts. */
    products: '/v1/merchant/discounts/products',
  },
  orders: {
    root: '/v1/merchant/orders',
    count: '/v1/merchant/orders/count',
    reportTelegram: '/v1/merchant/orders/report/telegram',
    reportExcel: '/v1/merchant/orders/report/export-excel',
  },
  diningTables: {
    root: '/v1/merchant/dining-tables',
    batch: '/v1/merchant/dining-tables/batch',
    /** Every table's QR on one A4 run. */
    bulkQrPdf: '/v1/merchant/dining-tables/download/qr/pdf/bulk',
  },
  /**
   * Point of Sale — registers, floor, drawers, exceptions and approvals.
   *
   * Its own tree under `/v1/merchant/pos`, gated on the `pos` module by the
   * membership-aware staff guard rather than by "does this user own a tenant".
   * The till writes the same records through `/v1/pos/registers`; this is the
   * back office's read of them.
   */
  pos: {
    registers: '/v1/merchant/pos/registers',
    /** The grid, already grouped by outlet and zone. Not paged. */
    floor: '/v1/merchant/pos/floor',
    tables: '/v1/merchant/pos/tables',
    zones: '/v1/merchant/pos/zones',
    sessions: '/v1/merchant/pos/sessions',
    exceptions: '/v1/merchant/pos/exceptions',
    approvalPolicy: '/v1/merchant/pos/approvals/policy',
    approvalLog: '/v1/merchant/pos/approvals/log',
  },
  /**
   * Customers — the shoppers who have granted this store permission.
   *
   * Read-only. The row behind each one is the customer's own act of granting
   * that permission, so there is nothing here for the back office to write.
   */
  customers: '/v1/merchant/customers',
  banners: '/v1/merchant/banners',
  pages: '/v1/merchant/pages',
  /** Open hours. The segment is `time-management`, not `open-hours`. */
  openHours: '/v1/merchant/time-management',
  shippingZones: '/v1/merchant/shippings/zone',
  tenant: {
    root: '/v1/merchant/tenants',
    branches: '/v1/merchant/tenants/branches',
  },
  /**
   * The Back Office dashboard.
   *
   * `overview` and `graph` are the original pair — fixed windows of order counts
   * for the home strip. The four below are the Dashboard module's own, gated on
   * the `dashboard` staff module rather than on tenant ownership, and split by
   * how fast each panel goes stale: the summary moves as orders land, the
   * registers as tills call home, the alerts when somebody acts.
   */
  dashboard: {
    overview: '/v1/merchant/dashboard/overview',
    graph: '/v1/merchant/dashboard/graph',
    summary: '/v1/merchant/dashboard/summary',
    registers: '/v1/merchant/dashboard/registers',
    alerts: '/v1/merchant/dashboard/alerts',
    acknowledgeAlert: '/v1/merchant/dashboard/alerts/acknowledge',
  },
  /**
   * Sales reports across outlets — the legacy POS report pack, rebuilt on
   * NexQR's own orders.
   *
   * Gated on the `reports` staff module, not `pos`: these are cross-outlet
   * takings, which a supervisor who may read one drawer's X should not get by
   * default. Each report has a JSON read and an `/export` of the same figures as
   * an Excel workbook, built by the server so the file and the screen are the
   * same answer.
   */
  reports: {
    salesSummary: '/v1/merchant/reports/sales-summary',
    salesSummaryExport: '/v1/merchant/reports/sales-summary/export',
    salesRegister: '/v1/merchant/reports/sales-register',
    salesRegisterExport: '/v1/merchant/reports/sales-register/export',
    itemSales: '/v1/merchant/reports/item-sales',
    itemSalesExport: '/v1/merchant/reports/item-sales/export',
    invoiceLines: '/v1/merchant/reports/invoice-lines',
    invoiceLinesExport: '/v1/merchant/reports/invoice-lines/export',
    settlement: '/v1/merchant/reports/settlement',
    settlementExport: '/v1/merchant/reports/settlement/export',
    auditLog: '/v1/merchant/reports/audit-log',
    auditLogExport: '/v1/merchant/reports/audit-log/export',
  },
  /** Uploads. Not under `/v1/merchant` — the file service is shared. */
  fileUpload: '/v1/files/upload',
} as const

/**
 * Routes of the inventory service, all under `/v1/inventory`.
 *
 * None of them take a tenant id: the service reads the tenant off the bearer
 * token. And every list refuses a query param it does not declare with a 400,
 * so an unset filter has to be left off rather than sent empty.
 */
export const inventoryEndpoints = {
  /** Stock items. Each row carries its stock totals across every location. */
  items: '/v1/inventory/items',
  /** The item behind a catalogue product: `/:productId`. A 404 means it is not tracked. */
  itemsByProduct: '/v1/inventory/items/by-product',
  /** The same, for one variant: `/:variantId`. */
  itemsByVariant: '/v1/inventory/items/by-variant',
  categories: '/v1/inventory/categories',
  /** Branches and warehouses. Read-only here — mirrored from the main API's branches. */
  locations: '/v1/inventory/locations',
  /** Levels per location. `itemIds` (at most 50) with a `locationId` is the batch read. */
  stock: '/v1/inventory/stock',
  /** Bills of materials, one per COMPOSITE item, lines embedded. */
  recipes: '/v1/inventory/recipes',
  /** Units of measure: the system's, then the business's own. */
  units: '/v1/inventory/units',
  /** Adjustments, wastage and opening balances: drafts, then `/:id/submit` (and `/approve` over the threshold). */
  adjustments: '/v1/inventory/adjustments',
  /** Stock counts: `/:id/lines/:itemId` to count a line, then `/:id/submit`. */
  counts: '/v1/inventory/counts',
  /** Why stock was wasted or adjusted: the shipped reasons and the business's own. */
  reasonCodes: '/v1/inventory/reason-codes',
  /** The stock ledger: every movement in or out, newest first. */
  movements: '/v1/inventory/movements',
  /**
   * The inventory reports, read live from the ledger. They filter by inventory
   * `locationIds`, not branch ids — the report API translates one to the other.
   */
  reports: {
    summary: '/v1/inventory/reports/summary',
    stockOnHand: '/v1/inventory/reports/stock-on-hand',
    movementSummary: '/v1/inventory/reports/movement-summary',
    /** The blind sheet for one `locationId`. */
    countSheet: '/v1/inventory/reports/count-sheet',
    /** The register; `counts/:id` is one count's variance. */
    counts: '/v1/inventory/reports/counts',
    transfers: '/v1/inventory/reports/transfers',
    adjustments: '/v1/inventory/reports/adjustments',
    recipeCosting: '/v1/inventory/reports/recipe-costing',
  },
} as const
